import { useMediaQuery } from 'usehooks-ts';
import React, { useContext, useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';

import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../../contexts/player-chat';
import { PlayerAvatar } from '../player-avatar';
import { GameContext } from '../../../contexts/game';
import { PlayerContext } from '../../../contexts/player';
import { OverlayContext } from '../../../contexts/overlay';
import { TradeStateContext } from '../../../contexts/trade';
import { useFullscreen } from '../../../hooks/use-fullscreen';
import { GameDocumentContext } from '../../../contexts/game-document';
import { TimerProgressPill } from '../../../components/timer/progress-pill';
import { QRScannerModal } from '../../../components/dialog/qr-scanner-dialog';
import AchievementDialog from '../../../components/dialog/achievement-dialog';
import { NotificationIndicator } from '../../../components/notification-indicator';

interface IconMenuProps {
  icon: string;
  isDisabled?: boolean;
  notificationCount?: number;
  onClick: () => void;
}

const IconMenu: React.FC<IconMenuProps> = ({
  icon,
  isDisabled,
  notificationCount = 0,
  onClick
}) => {
  return (
    <div className={'position-relative'}>
      <Button
        className={'text-white'}
        fillMode={'flat'}
        themeColor={'primary'}
        size={'medium'}
        onClick={onClick}
        {...(isDisabled !== undefined && {
          disabled: isDisabled
        })}>
        <div className={'d-flex'}>
          <span
            className={'material-symbols-outlined'}
            style={{
              fontSize: '2rem',
              filter: 'drop-shadow(0px 3px 1px #00000029)'
            }}>
            {icon}
          </span>
        </div>
      </Button>
      {notificationCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0
          }}>
          <NotificationIndicator
            counter={notificationCount}
            bgColor={'#FF5527'}
          />
        </div>
      )}
    </div>
  );
};

export const Menu = () => {
  const { newChat } = useContext(PlayerChatContext) as PlayerChatContextProps;
  const [player] = useContext(PlayerContext);
  const [state] = useContext(GameDocumentContext);
  const [game, , , isOnline] = useContext(GameContext);
  const [overlay, setOverlay] = useContext(OverlayContext);
  const [trade] = useContext(TradeStateContext);

  const [showScanner, setShowScanner] = React.useState(false);
  const [showInvalidScanner, setShowInvalidScanner] = React.useState(false);

  const isSmallDevice = useMediaQuery('(max-width:767px)');
  const { isFullscreen, enterFullscreen, exitFullscreen } = useFullscreen();

  const isOnTradingSession = useMemo(() => {
    const offer = ['Draft', 'Pending'].includes(trade.offerPlayerStatus);
    const request = ['Draft', 'Pending'].includes(trade.requestPlayerStatus);
    return (
      [offer, request].every((e) => e) ||
      [trade.offerPlayerStatus, trade.requestPlayerStatus].some(
        (e) => e === 'Accepted'
      )
    );
  }, [trade.offerPlayerStatus, trade.requestPlayerStatus]);

  const playerCode = player.playerState?.code ?? '';
  const isInLobby = ['waiting', 'ready'].includes(
    player.playerState?.status ?? ''
  );
  const preGameSetting = state.gameDocument?.settings.preGame;
  const inGameSetting =
    game.gameState?.runtime?.inGame ?? state.gameDocument?.settings.inGame;

  const showChatTab = isInLobby
    ? preGameSetting?.showPreGameChat
    : inGameSetting?.showInGameChat;

  const openChat = () => {
    setOverlay((prevState) => ({
      ...prevState,
      activeTab: 'chat',
      drawerIsOpen: true
    }));
  };

  const toggleOverlay = () => {
    setOverlay((prevState) => ({
      ...prevState,
      drawerIsOpen: !prevState.drawerIsOpen
    }));
  };

  const toggleOverlayMobile = () => {
    setOverlay((prevState) => ({
      ...prevState,
      drawerIsOpen: true,
      drawerMobileIsOpen: !prevState.drawerMobileIsOpen
    }));
  };

  const toggleProfileDialog = () =>
    setOverlay((prev) => ({
      ...prev,
      profileDialogIsOpen: !prev.profileDialogIsOpen
    }));

  const toggleButton = useMemo(() => {
    if (!isSmallDevice)
      return [
        {
          icon: overlay.drawerIsOpen ? 'expand_less' : 'menu',
          onClick: toggleOverlay,
          notificationCount: 0
        }
      ];

    return overlay.drawerIsOpen
      ? [
          {
            icon: 'expand_less',
            onClick: toggleOverlay,
            notificationCount: 0
          }
        ]
      : [];
  }, [isSmallDevice, overlay.drawerIsOpen]);

  const toggleButtonMobile = useMemo(
    () =>
      isSmallDevice
        ? [
            {
              icon: 'menu',
              onClick: toggleOverlayMobile,
              notificationCount: 0
            }
          ]
        : [],
    [isSmallDevice]
  );

  const isDisplayScanner = React.useMemo(() => {
    if (!state.gameDocument) return false;
    return (state.gameDocument.assets.tasks ?? []).some((t) => t.enableQRCode);
  }, [state.gameDocument]);

  const isDisabledScanner = React.useMemo(() => {
    if (game.gameState && game.gameState.status !== 'Running') return true;
    if (player.playerState?.openedOnDemandTask) return true;
    if (
      player.playerState?.openedTask &&
      player.playerState.openedTask.length > 0
    )
      return true;

    return false;
  }, [
    game.gameState?.status,
    player.playerState?.openedOnDemandTask,
    player.playerState?.openedTask
  ]);

  React.useEffect(() => {
    const offlineOverlay = document.getElementById('offline-overlay');
    if (!offlineOverlay) return;

    if (isFullscreen && !isOnline) {
      offlineOverlay.classList.remove('d-none');
    } else {
      offlineOverlay.classList.add('d-none');
    }
  }, [isFullscreen, isOnline]);

  return (
    <>
      <div className={'w-100 position-absolute'}>
        <Container
          className={'px-3 px-xxl-0'}
          fluid
          style={{
            maxWidth: '1366px'
          }}>
          <Row className={'overflow-hidden container-header'}>
            <Col
              className={'d-flex header-left-menu align-content-center'}
              xs={12}
              md={'auto'}>
              <TimerProgressPill />
            </Col>

            <Col
              xs={12}
              md={'auto'}
              className={
                'd-flex header-right-menu align-content-center align-items-center justify-content-end'
              }>
              {[
                ...(showChatTab
                  ? [
                      {
                        icon: 'chat',
                        onClick: openChat,
                        notificationCount: newChat.filter(
                          (c) => c.author.id !== playerCode
                        ).length
                      }
                    ]
                  : []),
                ...toggleButton,
                ...toggleButtonMobile
              ].map(({ icon, notificationCount, onClick }) => (
                <IconMenu
                  key={icon}
                  icon={icon}
                  onClick={onClick}
                  notificationCount={notificationCount}
                  isDisabled={isOnTradingSession}
                />
              ))}

              {!isInLobby && isDisplayScanner && !isSmallDevice && (
                <Button
                  fillMode={'flat'}
                  className={'text-white'}
                  themeColor={'primary'}
                  onClick={() => setShowScanner(true)}
                  disabled={isDisabledScanner}>
                  <div
                    className={
                      'd-flex align-items-center justify-content-center gap-2'
                    }
                    style={{
                      filter: 'drop-shadow(0px 3px 1px #00000029)'
                    }}>
                    <span
                      className={'material-symbols-outlined'}
                      style={{
                        fontSize: '2rem'
                      }}>
                      qr_code_scanner
                    </span>
                    <span>QR scan</span>
                  </div>
                </Button>
              )}

              {!isInLobby && isDisplayScanner && isSmallDevice && (
                <IconMenu
                  icon={'qr_code_scanner'}
                  onClick={() => setShowScanner(true)}
                  isDisabled={isDisabledScanner}
                />
              )}

              {!isSmallDevice && <PlayerAvatar onClick={toggleProfileDialog} />}

              <IconMenu
                icon={isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {showScanner && (
        <QRScannerModal
          onClose={() => setShowScanner(false)}
          onInvalid={() => setShowInvalidScanner(true)}
        />
      )}

      {showInvalidScanner && (
        <AchievementDialog
          onClose={() => setShowInvalidScanner(false)}
          onMaskClick={() => setShowInvalidScanner(false)}>
          <p>QR code is invalid for this game</p>
        </AchievementDialog>
      )}
    </>
  );
};
